import { useMemo } from 'react';
import { Html } from '@react-three/drei';
import { Box3, Vector3, type Mesh, type Object3D } from 'three/webgpu';

import { COMPUTER_KEYBOARD_BASE_NOTE, NOTE_KEYS, PANEL_KEYS } from '../input/keymap';
import { useAppStore } from '../store/appStore';
import { useKeyboardStore } from '../store/keyboardStore';
import { useInstrumentStore } from './instrumentStore';

/** How far in from the front edge of a piano key its label sits, as a share of the key's length. */
const KEY_INSET = 0.18;

/** What a key is called on its cap, for the codes that are not a plain letter or digit. */
const KEY_LABELS: Record<string, string> = {
  Escape: 'ESC',
  Backspace: '⌫',
  Enter: 'ENTER',
  Space: 'SPACE',
  Tab: 'TAB',
  Comma: ',',
  Period: '.',
  Minus: '-',
  Equal: '=',
  Backslash: '\\',
  Backquote: '`',
  Slash: '/',
  BracketLeft: '[',
  BracketRight: ']',
  Semicolon: ';',
  Quote: "'",
  ArrowUp: '↑',
  ArrowDown: '↓',
};

interface Tag {
  id: string;
  label: string;
  position: Vector3;
  note: boolean;
}

function keyLabel(code: string): string {
  const plain = /^(?:Key|Digit)(\w)$/.exec(code);
  return plain ? plain[1] : (KEY_LABELS[code] ?? code);
}

/** A point given in a mesh's own space, expressed in the instrument's. */
function inInstrumentSpace(mesh: Mesh, point: Vector3, root: Object3D): Vector3 {
  return root.worldToLocal(mesh.localToWorld(point.clone()));
}

function boundsOf(mesh: Mesh): Box3 {
  if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
  return mesh.geometry.boundingBox as Box3;
}

/** The middle of a button's top face. */
function buttonAnchor(mesh: Mesh, root: Object3D): Vector3 {
  const box = boundsOf(mesh);
  const top = new Vector3((box.min.x + box.max.x) / 2, box.max.y, (box.min.z + box.max.z) / 2);
  return inInstrumentSpace(mesh, top, root);
}

/**
 * Near the front of a piano key's top face. Keys hinge at the back, so the
 * front is whichever end lies furthest from the mesh's own origin.
 */
function keyAnchor(mesh: Mesh, root: Object3D): Vector3 {
  const box = boundsOf(mesh);
  const length = box.max.z - box.min.z;
  const frontIsMax = Math.abs(box.max.z) > Math.abs(box.min.z);
  const z = frontIsMax ? box.max.z - length * KEY_INSET : box.min.z + length * KEY_INSET;
  const front = new Vector3((box.min.x + box.max.x) / 2, box.max.y, z);
  return inInstrumentSpace(mesh, front, root);
}

function panelTags(buttons: Map<string, Mesh>, root: Object3D): Tag[] {
  const tags: Tag[] = [];
  for (const [command, codes] of Object.entries(PANEL_KEYS)) {
    const mesh = buttons.get(command);
    if (!mesh) continue;
    tags.push({ id: command, label: keyLabel(codes[0]), position: buttonAnchor(mesh, root), note: false });
  }
  return tags;
}

function noteTags(keys: Map<number, Mesh>, octave: number, root: Object3D): Tag[] {
  const tags: Tag[] = [];
  for (const [code, semitone] of NOTE_KEYS) {
    const note = COMPUTER_KEYBOARD_BASE_NOTE + semitone + octave * 12;
    const mesh = keys.get(note);
    if (!mesh) continue;
    tags.push({ id: code, label: keyLabel(code), position: keyAnchor(mesh, root), note: true });
  }
  return tags;
}

/**
 * The computer-keyboard cheat sheet, laid over the instrument itself: a cap
 * above every button that has a key bound to it, and one on every piano key
 * the current octave reaches. Sits in the instrument's own frame, so the
 * labels ride the parallax with the panel.
 */
export function KeyGuide() {
  const open = useAppStore((state) => state.helpOpen);
  const phase = useAppStore((state) => state.phase);
  const octave = useKeyboardStore((state) => state.octave);
  const ready = useInstrumentStore((state) => state.ready);
  const root = useInstrumentStore((state) => state.root);
  const buttons = useInstrumentStore((state) => state.buttons);
  const keys = useInstrumentStore((state) => state.keys);

  const showing = open && ready && root !== null && phase === 'playing';

  const tags = useMemo(() => {
    if (!showing || !root) return [];
    root.updateMatrixWorld(true);
    return [...panelTags(buttons, root), ...noteTags(keys, octave, root)];
  }, [showing, root, buttons, keys, octave]);

  if (!showing) return null;

  return (
    <>
      {tags.map((tag) => (
        <Html
          key={tag.id}
          position={tag.position}
          center
          zIndexRange={[0, 0]}
          wrapperClass="keycap-anchor"
        >
          <span className={tag.note ? 'keycap keycap--note' : 'keycap'}>{tag.label}</span>
        </Html>
      ))}
    </>
  );
}
